import React, { type ComponentProps, useMemo, useState } from 'react';
import { type RouteProp, useRoute } from '@react-navigation/native';

import {
  LoadingAnimation,
  NotFoundView,
  RefreshErrorState,
} from '@order/components';
import {
  type SweetpassMembershipPlans,
  useSweetpassPlusPlans,
} from '@order/hooks';

import type { ModalStackParamList } from '../../../../navigation/AppNavigation.props';
import {
  useSubscribeToSweetpassPlus,
  useSweetpassCheckoutContentfulData,
} from '../../hooks';
import { SweetpassCheckoutScreenContent } from '../../SweetpassCheckoutScreenContent';
import { type SweetpassCheckoutVariationProps } from '../SweetpassCheckoutVariations.types';

// ────────────────────────────────────────────────────────────────────────────────

/**
 * Renders Sweetpass Checkout components for multiple membership plans.
 */
export const SweetpassCheckoutMultiplePlans = (
  props: SweetpassCheckoutVariationProps,
) => {
  const { isFetchingBaseData } = props;

  // ─── Membership Plans ────────────────────────────────────────────────

  const {
    plans,
    isLoadingPlans: isFetchingPlans,
    hasUnknownPlansError,
    defaultPlan,
  } = useSweetpassPlusPlans();
  const { monthly: monthlyPlan, annual: annualPlan } = plans;

  // ─── Flags ───────────────────────────────────────────────────────────

  const shouldRenderLoadingState = isFetchingBaseData || isFetchingPlans;

  // ─────────────────────────────────────────────────────────────────────

  if (shouldRenderLoadingState) {
    return <LoadingAnimation />;
  }

  if (hasUnknownPlansError) {
    return <RefreshErrorState />;
  }

  if (
    monthlyPlan === undefined ||
    annualPlan === undefined ||
    defaultPlan === undefined
  ) {
    return <NotFoundView />;
  }

  // ─────────────────────────────────────────────────────────────────────

  return (
    <SweetpassCheckoutMultiplePlansFulfilled
      annualPlan={annualPlan}
      monthlyPlan={monthlyPlan}
      defaultPlan={defaultPlan}
      {...props}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const SweetpassCheckoutMultiplePlansFulfilled = (
  props: SweetpassCheckoutMultiplePlansFulfilledProps,
) => {
  const {
    currentTierData,
    creditCardsData,
    onUpgradeSuccess,
    annualPlan,
    monthlyPlan,
    defaultPlan,
  } = props;

  // ─── Navigation ──────────────────────────────────────────────────────

  const route = useRoute<RouteProp<ModalStackParamList, 'SweetpassCheckout'>>();

  // ─── Default Membership Plan ID ──────────────────────────────────────

  const { planId, campaignId, redirectToAfterUpgrade } = route.params ?? {};

  const annualPlanID = annualPlan.id;
  const monthlyPlanID = monthlyPlan.id;

  const isProvidedPlanIdMatchesOneOfThePlans =
    annualPlanID === planId || monthlyPlanID === planId;
  const defaultMembershipPlanId = isProvidedPlanIdMatchesOneOfThePlans
    ? planId
    : defaultPlan.id;

  // ─── State ───────────────────────────────────────────────────────────

  const [selectedMembershipPlanId, setSelectedMembershipPlanId] =
    useState<string>(defaultMembershipPlanId);

  const selectedMembershipPlan =
    selectedMembershipPlanId === monthlyPlanID ? monthlyPlan : annualPlan;
  const selectedMembershipPlanPrice = Number(selectedMembershipPlan.price);

  // ─── Free Trial ──────────────────────────────────────────────────────

  const hasFreeTrial = Boolean(selectedMembershipPlan.hasFreeTrial);

  const isCustomerEligibleForFreeTrial = currentTierData.isEligibleForFreeTrial;
  const isFreeTrialEnabled = hasFreeTrial && isCustomerEligibleForFreeTrial;

  // ─── Content ─────────────────────────────────────────────────────────

  const { trialDuration, trialDurationSingular } = currentTierData;
  const { content, isLoading: isFetchingContent } =
    useSweetpassCheckoutContentfulData({
      isFreeTrialEnabled,
      planPrice: selectedMembershipPlanPrice,
      trialDuration,
      trialDurationSingular,
      billingFrequencyUnit: selectedMembershipPlan.billingFrequencyUnit,
    });

  const { title, termsAndConditions, ctaLabel, freeTrialNote } = content;

  // ─────────────────────────────────────────────────────────────────────

  const membershipPlansData = useMemo(() => {
    return {
      plans: { annual: annualPlan, monthly: monthlyPlan },
      selectedPlanId: selectedMembershipPlanId,
      onSelectedPlanIdChange: setSelectedMembershipPlanId,
      freeTrialNote,
    };
  }, [annualPlan, freeTrialNote, monthlyPlan, selectedMembershipPlanId]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleCta = useSubscribeToSweetpassPlus({
    planId: selectedMembershipPlan.id,
    campaignId,
    redirectToAfterUpgrade,
    hasFreeTrial,
    paymentId: creditCardsData.selectedCreditCard?.id,
    setHasCustomerBeenUpgraded: onUpgradeSuccess,
  });

  // ─── Flags ───────────────────────────────────────────────────────────

  const isInvalidPlan = !selectedMembershipPlan.id;

  // ─────────────────────────────────────────────────────────────────────

  if (isFetchingContent) {
    return <LoadingAnimation />;
  }

  if (isInvalidPlan) {
    return <NotFoundView />;
  }

  return (
    <SweetpassCheckoutScreenContent
      testID="sweetpass-checkout-screen"
      title={title}
      termsAndConditions={termsAndConditions}
      ctaLabel={ctaLabel}
      planPrice={selectedMembershipPlanPrice}
      membershipPlansData={membershipPlansData}
      hasFreeTrial={isFreeTrialEnabled}
      creditCardsData={creditCardsData}
      ctaHandler={handleCta}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

type SweetpassCheckoutMultiplePlansFulfilledProps = ComponentProps<
  typeof SweetpassCheckoutMultiplePlans
> & {
  defaultPlan: NonNullable<SweetpassMembershipPlans['monthly']>;
  annualPlan: NonNullable<SweetpassMembershipPlans['annual']>;
  monthlyPlan: NonNullable<SweetpassMembershipPlans['monthly']>;
};
